// MaterialsOnSiteSection.js
// ─────────────────────────────────────────────────────────────────────────────
// MATERIALS ON SITE (W9) — register of materials delivered to site but not yet
// incorporated into the works, certified under GCC clause 6.10.1 at the
// contract's MOS allowance (typically 80%).
//
// How it works
// ------------
//  • Register:  projects/{projectId}/materialsOnSite — one doc per material /
//    invoice line. Auto-numbered MOS-001, MOS-002, …
//  • Each line carries delivered qty × rate (or direct invoice value) and a
//    list of CONSUMPTION entries ({month, value}) recording draw-down as the
//    material is incorporated into the permanent works.
//  • Monthly W9 statement: for a selected month, per line —
//        delivered value to date  −  incorporated value to date  =  balance
//        certified = balance × allowance %
//    Totals across the register give the month's certified MOS value.
//  • "Push to Certificate" writes that value to the selected certificate doc's
//    `materialsOnSite` field — the same field the W1 PDF already prints on its
//    "Add 80% of Materials on Site (W9)" line. The register is the source of
//    truth; the certificate only stores the pushed output.
//
// Rounding discipline: all intermediate maths carries full floating-point
// precision; values are rounded at the display/output layer only.
// BOQ linkage follows the itemNo string convention (localeCompare numeric).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";
import SectionFilter from "./SectionFilter";
import { matchesSection } from "../utils/sections";

const C = tokens.color;
const MONO = '"JetBrains Mono", monospace';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtR = (v) =>
  "R " + numberOrZero(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtMonthLabel = (ym) => {
  if (!ym) return "—";
  const d = new Date(ym + "-01T00:00:00");
  if (isNaN(d)) return ym;
  return d.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
};

const thisMonth = () => new Date().toISOString().slice(0, 7);

const monthEnd = (ym) => `${ym}-31`; // string compare upper bound within the month

function nextMosRef(items) {
  const nums = items
    .map((m) => parseInt((m.ref || "").replace("MOS-", ""), 10))
    .filter((n) => Number.isFinite(n));
  return `MOS-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
}

// Delivered value of a line — qty × rate when both present, else direct value.
// Full precision; no rounding here.
function lineDeliveredValue(item) {
  const qty = numberOrZero(item.qtyDelivered);
  const rate = numberOrZero(item.rate);
  if (qty && rate) return qty * rate;
  return numberOrZero(item.invoiceValue);
}

// Consumption (incorporated) value up to and including a given month.
function consumedToDate(item, ym) {
  return (item.consumption || []).reduce(
    (s, c) => s + ((c.month || "") <= ym ? numberOrZero(c.value) : 0), 0
  );
}

// Line position at a month: delivered-to-date, consumed-to-date, balance, certified.
function linePosition(item, ym, defaultPct) {
  const delivered = (item.deliveryDate || "") <= monthEnd(ym) ? lineDeliveredValue(item) : 0;
  const consumed = Math.min(consumedToDate(item, ym), delivered);
  const balance = delivered - consumed;
  const pct = item.allowancePct === undefined || item.allowancePct === null || String(item.allowancePct) === ""
    ? defaultPct
    : numberOrZero(item.allowancePct);
  const certified = balance * (pct / 100);
  return { delivered, consumed, balance, pct, certified };
}

function lineStatus(item, ym) {
  const p = linePosition(item, ym, 80);
  if (p.delivered <= 0) return "Pending Delivery";
  if (p.balance <= 0.005) return "Fully Incorporated";
  if (p.consumed > 0) return "Partially Incorporated";
  return "On Site";
}

const STATUS_META = {
  "On Site":               { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Partially Incorporated":{ bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "Fully Incorporated":    { bg: C.successSoft, text: C.success, border: C.successBorder },
  "Pending Delivery":      { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
};

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnPrimary: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.primary, color: C.inkOnDark, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  thR: { padding: "8px 10px", textAlign: "right", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "7px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  tdR: { padding: "7px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top", textAlign: "right", fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 15, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

// ─── Consumption editor (portal modal) ──────────────────────────────────────

function ConsumptionModal({ item, onSave, onClose }) {
  const [rows, setRows] = useState(() =>
    (item.consumption || []).map((c) => ({ ...c, _id: c._id || `c_${Math.random().toString(36).slice(2)}` }))
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const delivered = lineDeliveredValue(item);
  const totalConsumed = rows.reduce((s2, r) => s2 + numberOrZero(r.value), 0);
  const over = totalConsumed > delivered + 0.005;

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { _id: `c_${Date.now().toString(36)}`, month: thisMonth(), value: "", note: "" }]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r._id !== id));

  const rate = numberOrZero(item.rate);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(680px, 100%)", maxHeight: "85vh", overflowY: "auto", padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ ...s.h3, margin: 0 }}>Incorporation — <span style={{ ...s.mono, color: C.primary }}>{item.ref}</span></h3>
            <p style={{ ...s.sub, margin: "2px 0 0" }}>{item.description}</p>
          </div>
          <button style={s.btn} onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize: 12, color: C.inkMuted, margin: "8px 0 14px" }}>
          Record value drawn down as the material is built into the permanent works, per certificate month.
          Delivered value <strong style={s.mono}>{fmtR(delivered)}</strong> · incorporated{" "}
          <strong style={{ ...s.mono, color: over ? C.danger : C.ink }}>{fmtR(totalConsumed)}</strong> · balance{" "}
          <strong style={s.mono}>{fmtR(delivered - totalConsumed)}</strong>
          {rate > 0 && <> · rate <strong style={s.mono}>{fmtR(rate)}</strong>/unit (qty × rate auto-fills value)</>}
        </p>
        {over && (
          <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: C.danger }}>
            ⚠ Incorporated value exceeds delivered value — the W9 will floor the balance at zero, but check the entries.
          </p>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={s.th}>Month</th>
              {rate > 0 && <th style={s.thR}>Qty</th>}
              <th style={s.thR}>Value (R)</th>
              <th style={s.th}>Note</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={rate > 0 ? 5 : 4} style={{ ...s.td, textAlign: "center", color: C.inkMuted, padding: 18 }}>No incorporation recorded — material is fully on site.</td></tr>
            ) : rows.map((r) => (
              <tr key={r._id}>
                <td style={s.td}>
                  <input type="month" style={s.inp} value={r.month || ""} onChange={(e) => setRow(r._id, { month: e.target.value })} />
                </td>
                {rate > 0 && (
                  <td style={s.td}>
                    <input type="number" style={{ ...s.inp, ...s.mono, textAlign: "right" }} value={r.qty ?? ""}
                      placeholder="qty"
                      onChange={(e) => {
                        const q = e.target.value;
                        setRow(r._id, { qty: q, value: q === "" ? r.value : String(numberOrZero(q) * rate) });
                      }} />
                  </td>
                )}
                <td style={s.td}>
                  <input type="number" style={{ ...s.inp, ...s.mono, textAlign: "right" }} value={r.value ?? ""}
                    onChange={(e) => setRow(r._id, { value: e.target.value })} />
                </td>
                <td style={s.td}>
                  <input style={s.inp} value={r.note || ""} placeholder="e.g. CH 2+340 – 2+580" onChange={(e) => setRow(r._id, { note: e.target.value })} />
                </td>
                <td style={s.td}><button style={s.btnDanger} onClick={() => removeRow(r._id)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <button style={s.btn} onClick={addRow}>+ Add Entry</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={s.btn} onClick={onClose}>Cancel</button>
            <button style={s.btnPrimary} onClick={() => {
              onSave(rows.map(({ _id, ...rest }) => ({ ...rest, value: String(numberOrZero(rest.value)) })));
              onClose();
            }}>Save Incorporation</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── W9 print modal ──────────────────────────────────────────────────────────

function PrintW9Modal({ month, rowsWithPos, totals, currentProject, defaultPct, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "32px 16px" }}>
      <div className="mos-print-sheet" style={{ background: "#fff", width: "min(900px, 100%)", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", padding: "36px 42px", color: "#0E1116", fontFamily: '"Inter", sans-serif' }}>
        <div className="mos-print-hide" style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 16 }}>
          <button style={s.btnPrimary} onClick={() => window.print()}>🖨 Print / Save PDF</button>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        <div style={{ borderBottom: `3px solid ${C.primary}`, paddingBottom: 12, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary }}>
              Statement of Materials on Site
            </p>
            <h1 style={{ margin: "4px 0 2px", fontSize: 20, letterSpacing: "-0.02em" }}>{fmtMonthLabel(month)}</h1>
            <p style={{ margin: 0, fontSize: 12, color: "#444" }}>
              {currentProject?.contractNumber} — {currentProject?.contractDescription}
            </p>
          </div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>W9</div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              {["Ref", "Description", "Supplier / Invoice", "BOQ Item"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "5px 7px", borderBottom: "1.5px solid #0E1116" }}>{h}</th>
              ))}
              {["Delivered to Date", "Incorporated to Date", "Balance on Site", "%", "Certified"].map((h) => (
                <th key={h} style={{ textAlign: "right", padding: "5px 7px", borderBottom: "1.5px solid #0E1116" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsWithPos.map(({ item, pos }) => (
              <tr key={item.id}>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", fontFamily: MONO }}>{item.ref}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd" }}>{item.description}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd" }}>{item.supplier}{item.invoiceNo ? ` / ${item.invoiceNo}` : ""}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", fontFamily: MONO }}>{item.linkedItemNo || "—"}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", textAlign: "right", fontFamily: MONO }}>{fmtR(pos.delivered)}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", textAlign: "right", fontFamily: MONO }}>{fmtR(pos.consumed)}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", textAlign: "right", fontFamily: MONO }}>{fmtR(pos.balance)}</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", textAlign: "right", fontFamily: MONO }}>{pos.pct}%</td>
                <td style={{ padding: "4px 7px", borderBottom: "1px solid #ddd", textAlign: "right", fontFamily: MONO, fontWeight: 700 }}>{fmtR(pos.certified)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={4} style={{ padding: "6px 7px", fontWeight: 800, borderTop: "2px solid #0E1116" }}>TOTAL</td>
              <td style={{ padding: "6px 7px", textAlign: "right", fontFamily: MONO, fontWeight: 800, borderTop: "2px solid #0E1116" }}>{fmtR(totals.delivered)}</td>
              <td style={{ padding: "6px 7px", textAlign: "right", fontFamily: MONO, fontWeight: 800, borderTop: "2px solid #0E1116" }}>{fmtR(totals.consumed)}</td>
              <td style={{ padding: "6px 7px", textAlign: "right", fontFamily: MONO, fontWeight: 800, borderTop: "2px solid #0E1116" }}>{fmtR(totals.balance)}</td>
              <td style={{ padding: "6px 7px", borderTop: "2px solid #0E1116" }}></td>
              <td style={{ padding: "6px 7px", textAlign: "right", fontFamily: MONO, fontWeight: 800, borderTop: "2px solid #0E1116" }}>{fmtR(totals.certified)}</td>
            </tr>
          </tbody>
        </table>

        <p style={{ fontSize: 10.5, color: "#444", marginTop: 14 }}>
          Materials on site are certified at the contract allowance (default {defaultPct}%) of the value of unfixed materials
          delivered to and properly stored on site, in terms of clause 6.10.1 of the General Conditions of Contract.
          The certified total above carries to line W9 of the Payment Certificate (W1 — Summary of Payments to Date).
        </p>

        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 10mm; }
            body * { visibility: hidden; }
            .mos-print-sheet, .mos-print-sheet * { visibility: visible; }
            .mos-print-sheet { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; }
            .mos-print-hide { display: none !important; }
          }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function MaterialsOnSiteSection({
  currentProject,
  currentProjectId,
  currentUser,
  soqItems = [],
  certificates = [],
}) {
  const projectId = currentProject?.id || currentProjectId;

  const [items, setItems] = useState([]);
  const [statementMonth, setStatementMonth] = useState(thisMonth());
  const [consumptionFor, setConsumptionFor] = useState(null); // item being edited
  const [printOpen, setPrintOpen] = useState(false);
  const [pushTargetId, setPushTargetId] = useState("");
  const [pushFlash, setPushFlash] = useState("");

  const defaultPct = numberOrZero(currentProject?.mosAllowancePct) || 80;

  // ── Subscription ──
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, "projects", projectId, "materialsOnSite"), orderBy("ref"));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [projectId]);

  // ── Audit log ──
  const logAudit = useCallback(async (action, entityId, summary) => {
    if (!projectId) return;
    try {
      await addDoc(collection(db, "projects", projectId, "auditLogs"), {
        entityType: "materialsOnSite",
        entityId: entityId || "",
        action,
        summary: summary || "",
        userName: currentUser?.displayName || "",
        userEmail: currentUser?.email || "",
        createdAt: serverTimestamp(),
      });
    } catch (e) { /* best-effort */ }
  }, [projectId, currentUser]);

  // ── CRUD ──
  const addItem = useCallback(async () => {
    if (!projectId) return;
    const ref = nextMosRef(items);
    const docRef = await addDoc(collection(db, "projects", projectId, "materialsOnSite"), {
      ref,
      description: "",
      supplier: "",
      invoiceNo: "",
      invoiceDate: "",
      deliveryDate: new Date().toISOString().slice(0, 10),
      linkedItemNo: "",
      unit: "",
      qtyDelivered: "",
      rate: "",
      invoiceValue: "",
      allowancePct: "",
      storageLocation: "",
      consumption: [],
      createdAt: serverTimestamp(),
      createdBy: currentUser?.email || "",
    });
    await logAudit("create", docRef.id, `Material ${ref} added to MOS register`);
  }, [projectId, items, currentUser, logAudit]);

  const saveField = useCallback(async (item, patch) => {
    if (!projectId) return;
    await updateDoc(doc(db, "projects", projectId, "materialsOnSite", item.id), {
      ...patch, updatedAt: serverTimestamp(),
    });
  }, [projectId]);

  const deleteItem = useCallback(async (item) => {
    if (!projectId) return;
    if (!window.confirm(`Delete ${item.ref} (${item.description || "no description"}) from the MOS register? This cannot be undone.`)) return;
    await deleteDoc(doc(db, "projects", projectId, "materialsOnSite", item.id));
    await logAudit("delete", item.id, `Material ${item.ref} deleted from MOS register`);
  }, [projectId, logAudit]);

  const saveConsumption = useCallback(async (item, rows) => {
    await saveField(item, { consumption: rows });
    await logAudit("update", item.id, `Incorporation updated on ${item.ref} (${rows.length} entr${rows.length === 1 ? "y" : "ies"})`);
  }, [saveField, logAudit]);

  // ── Local inline-edit buffer: edit on change, persist on blur ──
  const [edits, setEdits] = useState({}); // { [itemId]: { field: value } }
  const editVal = (item, field) =>
    edits[item.id]?.[field] !== undefined ? edits[item.id][field] : (item[field] ?? "");
  const onEdit = (item, field, value) =>
    setEdits((e) => ({ ...e, [item.id]: { ...(e[item.id] || {}), [field]: value } }));
  const onCommit = (item, field) => {
    const v = edits[item.id]?.[field];
    if (v === undefined || v === (item[field] ?? "")) return;
    saveField(item, { [field]: v });
    setEdits((e) => {
      const next = { ...(e[item.id] || {}) };
      delete next[field];
      return { ...e, [item.id]: next };
    });
  };

  // ── Statement (W9) computation — full precision throughout ──
  const rowsWithPos = useMemo(() =>
    items
      .map((item) => ({ item, pos: linePosition(item, statementMonth, defaultPct) }))
      .filter(({ pos }) => pos.delivered > 0),
    [items, statementMonth, defaultPct]
  );

  const totals = useMemo(() => rowsWithPos.reduce(
    (t, { pos }) => ({
      delivered: t.delivered + pos.delivered,
      consumed: t.consumed + pos.consumed,
      balance: t.balance + pos.balance,
      certified: t.certified + pos.certified,
    }),
    { delivered: 0, consumed: 0, balance: 0, certified: 0 }
  ), [rowsWithPos]);

  // ── Certificate push ──
  const sortedCerts = useMemo(() =>
    [...certificates].sort((a, b) => numberOrZero(a.certificateNo) - numberOrZero(b.certificateNo)),
    [certificates]
  );

  // Default target: certificate whose month matches the statement month
  useEffect(() => {
    const match = sortedCerts.find((c) => (c.month || c.certificateMonth || "") === statementMonth);
    setPushTargetId(match ? match.id : (sortedCerts.length ? sortedCerts[sortedCerts.length - 1].id : ""));
  }, [statementMonth, sortedCerts]);

  const pushToCertificate = useCallback(async () => {
    if (!projectId || !pushTargetId) return;
    const cert = sortedCerts.find((c) => c.id === pushTargetId);
    if (!cert) return;
    const value = totals.certified; // full precision stored; W1 rounds at output
    if (!window.confirm(
      `Push ${fmtR(value)} (certified MOS for ${fmtMonthLabel(statementMonth)}) to Certificate No. ${cert.certificateNo}?\n\nThis overwrites the certificate's Materials on Site (W9) value.`
    )) return;
    await updateDoc(doc(db, "projects", projectId, "certificates", cert.id), {
      materialsOnSite: value,
      materialsOnSiteMonth: statementMonth,
      materialsOnSiteUpdatedAt: serverTimestamp(),
    });
    await logAudit("update", cert.id, `MOS ${fmtR(value)} (${statementMonth}) pushed to Certificate No. ${cert.certificateNo}`);
    setPushFlash(`✓ Pushed to Cert No. ${cert.certificateNo}`);
    setTimeout(() => setPushFlash(""), 2500);
  }, [projectId, pushTargetId, sortedCerts, totals.certified, statementMonth, logAudit]);

  // ── SOQ datalist for itemNo linkage ──
  const soqOptions = useMemo(() =>
    [...soqItems]
      .map((i) => i.itemNo)
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })),
    [soqItems]
  );

  // ── Section filter: materials inherit the section of their linked BOQ item ──
  const [materialSectionFilter, setMaterialSectionFilter] = useState("All");
  const sectionByItemNo = useMemo(() => {
    const m = {};
    (soqItems || []).forEach((i) => {
      const k = String(i.itemNo || "").trim();
      if (k) m[k] = { sectionNo: i.sectionNo || "", sectionName: i.sectionName || "" };
    });
    return m;
  }, [soqItems]);
  const visibleMaterials = useMemo(() =>
    (items || []).filter((it) =>
      matchesSection(sectionByItemNo[String(it.linkedItemNo || "").trim()] || {}, materialSectionFilter)
    ),
    [items, sectionByItemNo, materialSectionFilter]
  );

  if (!projectId) {
    return (
      <section style={s.card}>
        <h2 style={s.title}>Materials on Site</h2>
        <p style={s.sub}>Select a project to manage the MOS register.</p>
      </section>
    );
  }

  const liveTotals = items.reduce((t, item) => {
    const pos = linePosition(item, thisMonth(), defaultPct);
    return { balance: t.balance + pos.balance, certified: t.certified + pos.certified, count: t.count + (pos.balance > 0.005 ? 1 : 0) };
  }, { balance: 0, certified: 0, count: 0 });

  return (
    <div style={s.page}>
      {/* ── Register ── */}
      <section style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={s.title}>Materials on Site</h2>
            <p style={s.sub}>
              Register of unfixed materials delivered to site (GCC 6.10.1). Certified at {defaultPct}% of balance;
              the monthly statement below carries to line W9 of the payment certificate.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SectionFilter items={soqItems} value={materialSectionFilter} onChange={setMaterialSectionFilter} />
            <button style={s.btnAccent} onClick={addItem}>+ Add Material</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10, marginBottom: 16 }}>
          <div style={s.statTile}><p style={s.statLabel}>Lines with Balance</p><p style={s.statValue}>{liveTotals.count}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Balance on Site (now)</p><p style={s.statValue}>{fmtR(liveTotals.balance)}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Certified @ {defaultPct}% (now)</p><p style={s.statValue}>{fmtR(liveTotals.certified)}</p></div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={s.th}>Ref</th>
                <th style={{ ...s.th, minWidth: 200 }}>Description</th>
                <th style={s.th}>Supplier</th>
                <th style={s.th}>Invoice No</th>
                <th style={s.th}>Delivered</th>
                <th style={s.th}>BOQ Item</th>
                <th style={s.thR}>Qty</th>
                <th style={s.thR}>Rate</th>
                <th style={s.thR}>Invoice Value</th>
                <th style={s.thR}>%</th>
                <th style={s.thR}>Balance (now)</th>
                <th style={s.th}>Status</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {visibleMaterials.length === 0 ? (
                <tr><td colSpan={13} style={{ ...s.td, textAlign: "center", color: C.inkMuted, padding: 24 }}>
                  {items.length === 0 ? "No materials registered — add the first invoice line above." : "No materials in this section."}
                </td></tr>
              ) : visibleMaterials.map((item) => {
                const pos = linePosition(item, thisMonth(), defaultPct);
                const status = lineStatus(item, thisMonth());
                return (
                  <tr key={item.id}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 800, color: C.primary, whiteSpace: "nowrap" }}>{item.ref}</td>
                    <td style={s.td}>
                      <input style={s.inp} value={editVal(item, "description")} placeholder="e.g. 600mm dia steel pipe, epoxy lined"
                        onChange={(e) => onEdit(item, "description", e.target.value)} onBlur={() => onCommit(item, "description")} />
                    </td>
                    <td style={s.td}>
                      <input style={{ ...s.inp, minWidth: 110 }} value={editVal(item, "supplier")}
                        onChange={(e) => onEdit(item, "supplier", e.target.value)} onBlur={() => onCommit(item, "supplier")} />
                    </td>
                    <td style={s.td}>
                      <input style={{ ...s.inp, ...s.mono, minWidth: 95 }} value={editVal(item, "invoiceNo")}
                        onChange={(e) => onEdit(item, "invoiceNo", e.target.value)} onBlur={() => onCommit(item, "invoiceNo")} />
                    </td>
                    <td style={s.td}>
                      <input type="date" style={{ ...s.inp, minWidth: 130 }} value={editVal(item, "deliveryDate")}
                        onChange={(e) => onEdit(item, "deliveryDate", e.target.value)} onBlur={() => onCommit(item, "deliveryDate")} />
                    </td>
                    <td style={s.td}>
                      <input list="mos-soq-items" style={{ ...s.inp, ...s.mono, minWidth: 90 }} value={editVal(item, "linkedItemNo")}
                        placeholder="itemNo"
                        onChange={(e) => onEdit(item, "linkedItemNo", e.target.value)} onBlur={() => onCommit(item, "linkedItemNo")} />
                    </td>
                    <td style={s.td}>
                      <input type="number" style={{ ...s.inp, ...s.mono, minWidth: 80, textAlign: "right" }} value={editVal(item, "qtyDelivered")}
                        onChange={(e) => onEdit(item, "qtyDelivered", e.target.value)} onBlur={() => onCommit(item, "qtyDelivered")} />
                    </td>
                    <td style={s.td}>
                      <input type="number" style={{ ...s.inp, ...s.mono, minWidth: 90, textAlign: "right" }} value={editVal(item, "rate")}
                        onChange={(e) => onEdit(item, "rate", e.target.value)} onBlur={() => onCommit(item, "rate")} />
                    </td>
                    <td style={s.td}>
                      <input type="number" style={{ ...s.inp, ...s.mono, minWidth: 110, textAlign: "right" }} value={editVal(item, "invoiceValue")}
                        placeholder={pos.delivered ? String(pos.delivered) : "value"}
                        title="Used when qty × rate are not captured"
                        onChange={(e) => onEdit(item, "invoiceValue", e.target.value)} onBlur={() => onCommit(item, "invoiceValue")} />
                    </td>
                    <td style={s.td}>
                      <input type="number" style={{ ...s.inp, ...s.mono, minWidth: 60, textAlign: "right" }} value={editVal(item, "allowancePct")}
                        placeholder={String(defaultPct)}
                        onChange={(e) => onEdit(item, "allowancePct", e.target.value)} onBlur={() => onCommit(item, "allowancePct")} />
                    </td>
                    <td style={s.tdR}>{fmtR(pos.balance)}</td>
                    <td style={s.td}><span style={s.chip(STATUS_META[status])}>{status}</span></td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      <button style={{ ...s.btn, padding: "6px 10px", fontSize: 11.5, marginRight: 6 }} onClick={() => setConsumptionFor(item)}>
                        Incorporate{(item.consumption || []).length ? ` (${item.consumption.length})` : ""}
                      </button>
                      <button style={s.btnDanger} onClick={() => deleteItem(item)}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="mos-soq-items">
            {soqOptions.map((no) => <option key={no} value={no} />)}
          </datalist>
        </div>
      </section>

      {/* ── Monthly W9 statement ── */}
      <section style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...s.title, fontSize: 19 }}>W9 Statement — {fmtMonthLabel(statementMonth)}</h2>
            <p style={s.sub}>Position at month end: delivered to date, incorporated to date, balance, certified value.</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label style={s.lbl}>Statement Month</label>
              <input type="month" style={s.inp} value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} />
            </div>
            <button style={s.btn} onClick={() => setPrintOpen(true)}>🖨 Print W9</button>
          </div>
        </div>

        <div style={{ overflowX: "auto", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={s.th}>Ref</th>
                <th style={{ ...s.th, minWidth: 200 }}>Description</th>
                <th style={s.thR}>Delivered to Date</th>
                <th style={s.thR}>Incorporated to Date</th>
                <th style={s.thR}>Balance on Site</th>
                <th style={s.thR}>%</th>
                <th style={s.thR}>Certified</th>
              </tr>
            </thead>
            <tbody>
              {rowsWithPos.length === 0 ? (
                <tr><td colSpan={7} style={{ ...s.td, textAlign: "center", color: C.inkMuted, padding: 24 }}>
                  No materials delivered on or before {fmtMonthLabel(statementMonth)}.
                </td></tr>
              ) : rowsWithPos.map(({ item, pos }) => (
                <tr key={item.id} style={{ opacity: pos.balance <= 0.005 ? 0.55 : 1 }}>
                  <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{item.ref}</td>
                  <td style={s.td}>{item.description || "—"}</td>
                  <td style={s.tdR}>{fmtR(pos.delivered)}</td>
                  <td style={s.tdR}>{fmtR(pos.consumed)}</td>
                  <td style={s.tdR}>{fmtR(pos.balance)}</td>
                  <td style={s.tdR}>{pos.pct}%</td>
                  <td style={{ ...s.tdR, fontWeight: 800 }}>{fmtR(pos.certified)}</td>
                </tr>
              ))}
              {rowsWithPos.length > 0 && (
                <tr style={{ background: C.surfaceAlt }}>
                  <td colSpan={2} style={{ ...s.td, fontWeight: 800, borderTop: `2px solid ${C.borderStrong}` }}>TOTAL</td>
                  <td style={{ ...s.tdR, fontWeight: 800, borderTop: `2px solid ${C.borderStrong}` }}>{fmtR(totals.delivered)}</td>
                  <td style={{ ...s.tdR, fontWeight: 800, borderTop: `2px solid ${C.borderStrong}` }}>{fmtR(totals.consumed)}</td>
                  <td style={{ ...s.tdR, fontWeight: 800, borderTop: `2px solid ${C.borderStrong}` }}>{fmtR(totals.balance)}</td>
                  <td style={{ ...s.td, borderTop: `2px solid ${C.borderStrong}` }}></td>
                  <td style={{ ...s.tdR, fontWeight: 800, color: C.primary, borderTop: `2px solid ${C.borderStrong}` }}>{fmtR(totals.certified)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Push to certificate */}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: "12px 14px", background: C.primarySoft, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Target Certificate (W1 line W9)</label>
            <select style={{ ...s.inp, minWidth: 220 }} value={pushTargetId} onChange={(e) => setPushTargetId(e.target.value)}>
              <option value="">— select certificate —</option>
              {sortedCerts.map((c) => (
                <option key={c.id} value={c.id}>
                  Cert No. {c.certificateNo}{(c.month || c.certificateMonth) ? ` — ${c.month || c.certificateMonth}` : ""}
                </option>
              ))}
            </select>
          </div>
          <button style={s.btnPrimary} onClick={pushToCertificate} disabled={!pushTargetId || rowsWithPos.length === 0}>
            Push {fmtR(totals.certified)} to Certificate
          </button>
          {pushFlash && <span style={{ fontSize: 12, fontWeight: 700, color: C.success, paddingBottom: 9 }}>{pushFlash}</span>}
        </div>
        <p style={{ ...s.sub, marginTop: 8 }}>
          The register is the source of truth — the certificate stores only the pushed output. Re-push after any register change for that month.
        </p>
      </section>

      {consumptionFor && (
        <ConsumptionModal
          item={consumptionFor}
          onSave={(rows) => saveConsumption(consumptionFor, rows)}
          onClose={() => setConsumptionFor(null)}
        />
      )}

      {printOpen && (
        <PrintW9Modal
          month={statementMonth}
          rowsWithPos={rowsWithPos}
          totals={totals}
          currentProject={currentProject}
          defaultPct={defaultPct}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </div>
  );
}

export default React.memo(MaterialsOnSiteSection);
